import { useState, type ReactNode } from 'react';
import { Pressable, View } from 'react-native';
import { falAngleRight } from 'shared/@generated/icons/falAngleRight';
import { falCheck } from 'shared/@generated/icons/falCheck';
import { falCircleInfo } from 'shared/@generated/icons/falCircleInfo';
import { Icon } from 'shared/components/icon';
import { Image } from 'shared/components/image';
import { Link } from 'shared/components/link';
import { Text } from 'shared/components/text';
import {
  getAdvisorCardCopy,
  getAdvisorMatchScore,
  type AdvisorCard,
  type AdvisorTranslate,
} from 'shared/library/breed-advisor';
import { useRoute } from 'shared/library/routes/route';

export function BreedAdvisorMatch({
  card,
  copy,
  children,
  warnings,
}: {
  card: AdvisorCard;
  copy: AdvisorTranslate;
  children?: ReactNode;
  warnings?: ReactNode;
}) {
  const route = useRoute();
  const content = getAdvisorCardCopy(card, copy);
  const score = getAdvisorMatchScore(card.score);
  const [open, setOpen] = useState(false);
  const [scoreExplanationOpen, setScoreExplanationOpen] = useState(false);
  return (
    <View testID={`advisor-match-${card.breed.id}`}>
      <Pressable
        testID={`advisor-match-toggle-${card.breed.id}`}
        accessibilityRole="button"
        accessibilityLabel={`${card.breed.name}, ${score === null ? copy('match.score_unavailable') : copy('match.score_accessibility', { score })}`}
        accessibilityState={{ expanded: open }}
        aria-expanded={open}
        onPress={() => setOpen(!open)}
        className="focus-visible:ring-brand-green group w-full flex-row items-center gap-3 p-3 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset"
      >
        <Image
          src={card.breed.avatar}
          alt={card.breed.name}
          containerClassName="aspect-4/3 w-16 shrink-0 md:w-20"
          imageClassName="rounded object-cover"
          sizeType="card"
          aspectRatio={4 / 3}
          sizes="(min-width: 768px) 80px, 64px"
        />
        <View className="min-w-0 flex-1 gap-1">
          <View className="flex-row items-center justify-between gap-2">
            <Text className="text-brand-brown-dark min-w-0 shrink text-sm font-semibold leading-5 group-hover:underline md:text-base">
              {card.breed.name}
            </Text>
            <View testID={`advisor-score-${card.breed.id}`} className="shrink-0">
              {score === null ? (
                <Text className="text-brand-brown-dark/65 max-w-24 text-xs">
                  {copy('match.score_unavailable')}
                </Text>
              ) : (
                <Text
                  testID={`advisor-score-value-${card.breed.id}`}
                  className="text-brand-brown-dark text-base font-semibold tabular-nums"
                >
                  {score}
                  <Text className="text-brand-brown-dark/45 text-xs font-normal">/100</Text>
                </Text>
              )}
            </View>
          </View>
          <View className="flex-row items-center gap-2">
            <Text
              numberOfLines={open ? undefined : 2}
              className="text-brand-brown-dark/65 min-w-0 flex-1 text-xs leading-5"
            >
              {content.summary}
            </Text>
            <Icon
              icon={falAngleRight}
              className={`text-brand-brown-dark/45 size-3 shrink-0 transition-transform ${open ? 'rotate-90' : ''}`}
            />
          </View>
        </View>
      </Pressable>
      {open && (
        <View
          testID={`advisor-match-details-${card.breed.id}`}
          className="border-brand-brown-dark/10 gap-3 border-t px-3 py-4"
        >
          {card.is_favorite && (
            <Text className="text-brand-green-dark text-xs font-medium">
              {copy('favorite_badge')}
            </Text>
          )}
          {!!content.positives.length && (
            <View className="gap-1">
              <Text className="text-brand-green-dark text-xs font-semibold">{copy('why_fit')}</Text>
              {content.positives.map((text, i) => (
                <View key={i} className="flex-row items-center gap-2">
                  <Icon icon={falCheck} className="text-brand-green-dark size-3.5 shrink-0" />
                  <Text className="text-brand-brown-dark/80 text-sm leading-5">{text}</Text>
                </View>
              ))}
            </View>
          )}
          {!!content.tradeoffs.length && (
            <View className="gap-1">
              <Text className="text-brand-brown-dark text-xs font-semibold">
                {copy('tradeoffs')}
              </Text>
              {content.tradeoffs.map((text, i) => (
                <Text key={i} className="text-brand-brown-dark/80 text-sm leading-5">
                  {text}
                </Text>
              ))}
            </View>
          )}
          <View className="gap-2">
            <Pressable
              accessibilityRole="button"
              accessibilityState={{ expanded: scoreExplanationOpen }}
              aria-expanded={scoreExplanationOpen}
              onPress={() => setScoreExplanationOpen(!scoreExplanationOpen)}
              className="group min-h-7 flex-row items-center gap-1.5 self-start"
            >
              <Text className="text-brand-brown-dark/65 group-hover:text-brand-brown-dark text-xs font-medium">
                {copy('match.score_label')}
              </Text>
              <Icon
                icon={falCircleInfo}
                className="text-brand-brown-dark/65 group-hover:text-brand-brown-dark size-3.5"
              />
            </Pressable>
            {score !== null && (
              <View
                accessibilityRole="progressbar"
                accessibilityLabel={copy('match.score_accessibility', { score })}
                accessibilityValue={{ min: 0, max: 100, now: score }}
                aria-valuemin={0}
                aria-valuemax={100}
                aria-valuenow={score}
                className="h-1.5 overflow-hidden rounded-full bg-stone-100"
              >
                <View
                  className={`h-full rounded-full ${card.assessment === 'possible' ? 'bg-brand-green' : 'bg-stone-400'}`}
                  style={{ width: `${score}%` }}
                />
              </View>
            )}
            {score !== null && card.assessment === 'insufficient' && (
              <Text className="text-brand-brown-dark/65 text-xs">
                {copy('match.score_limited')}
              </Text>
            )}
            {scoreExplanationOpen && (
              <Text className="text-brand-brown-dark/70 text-xs leading-5">
                {copy('match.score_explanation')}
              </Text>
            )}
          </View>
          {warnings ??
            (!!content.data_warnings.length && (
              <Text className="text-brand-brown-dark/65 text-xs leading-5">
                {content.data_warnings.join('\n\n')}
              </Text>
            ))}
          {!!card.data_sources?.length && (
            <View className="items-start gap-1">
              {card.data_sources.map((source) => (
                <Link
                  key={source.url}
                  href={source.url}
                  openInNewTab
                  textClassName="text-brand-brown-dark/65 text-xs underline"
                >
                  {source.url.startsWith('https://www.skk.se/')
                    ? 'Svenska Kennelklubben'
                    : 'American Kennel Club'}
                </Link>
              ))}
            </View>
          )}
          <Link
            href={route('breeds.show', { breed: card.breed.slug })}
            textClassName="text-brand-brown-dark text-sm font-medium underline"
          >
            {copy('view_breed')}
          </Link>
          {children}
        </View>
      )}
    </View>
  );
}
